import { useEffect, useMemo } from "react";
import useCalendar from "hooks/useCalendar";
import type { CalendarEvent } from "../@types/sharedSchema";

/**
 * The signed-in user's OWN calendar events, grouped by `listingId`.
 *
 * Every listing-scoped events surface (the property file's events tab, the
 * listings-board badge) used to read ONLY the org-stamped collectionGroup
 * query — `where("organizationIds", "array-contains", myOrgId)`. That made a
 * realtor's own task invisible on the very listing they created it from
 * whenever the event carried no `organizationIds`, which happens in three
 * ways:
 *   - the realtor is in no organization at all (`augmentCalendarEvent` stamps
 *     `[]`, and the dashboard resolves no `organizationId` to query with);
 *   - they joined their agency AFTER creating the event — the stamp is written
 *     once by the `updateCalendarEventFields` Cloud Function and never
 *     backfilled;
 *   - they are listed only under the org's `admins`, so the dashboard's
 *     `users array-contains` lookup resolves no org for them.
 * Measured on prod 2026-08-26: 388 of 843 listing-linked events, across 88
 * realtors, were invisible to the person who created them.
 *
 * The source here is CalendarContext's own-calendar listener — the exact list
 * the calendar page renders — so "it's in my calendar but not in the property
 * file" stops being expressible. It costs no extra Firestore reads on the
 * calendar page and one already-shared listener anywhere else.
 */
export function useMyEventsByListing(): {
  eventsByListing: Map<string, CalendarEvent[]>;
  isLoading: boolean;
} {
  const { events, initiateLoadEvents } = useCalendar();

  useEffect(() => {
    // Idempotent: the context subscribes once and shares the listener.
    initiateLoadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventsByListing = useMemo(() => {
    const next = new Map<string, CalendarEvent[]>();
    for (const event of events ?? []) {
      for (const listingId of event.listingIds ?? []) {
        if (!listingId) continue;
        const bucket = next.get(listingId);
        if (bucket) bucket.push(event);
        else next.set(listingId, [event]);
      }
    }
    return next;
  }, [events]);

  // `events` is null until the context's listener delivers its first snapshot.
  return { eventsByListing, isLoading: events === null };
}

/**
 * Union of several event lists, de-duplicated by document path (`id` as a
 * fallback). An own event that IS org-stamped comes back from both the
 * personal listener and the org one; earlier lists win, so pass the personal
 * list first.
 */
export function mergeCalendarEvents(...lists: CalendarEvent[][]): CalendarEvent[] {
  const byKey = new Map<string, CalendarEvent>();
  let anonymous = 0;
  for (const list of lists) {
    for (const event of list) {
      const key = event.path ?? event.id ?? `anon:${anonymous++}`;
      if (byKey.has(key)) continue;
      byKey.set(key, event);
    }
  }
  return Array.from(byKey.values());
}
